import React, { useEffect, useRef } from 'react'
import { createRoot } from 'react-dom/client'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { markText, unmarkText } from 'helpers/textFragment'
import url from 'libraries/front-end-core/core/url'
import { scrollTo } from 'libraries/front-end-core/helpers/document'
import { QA_CONTENT } from 'constants/controllers'
import { parseHtmlContent } from 'helpers/parseHtmlContent'
import selectFlavour from 'redux/selectors/selectFlavour'
import selectAolName from 'redux/selectors/selectAolName'
import selectTextFragment from 'redux/selectors/selectTextFragment'
import selectHighlight from 'redux/selectors/selectHighlight'
import selectPageId from 'redux/selectors/selectPageId'
import selectDeviceType from 'redux/selectors/selectDeviceType'
import Icon from './Icon'

const INTERNAL_DOCUMENT_HREF = /href="#([^"]+)"/g

const DOCUMENT_HREF = /href="([^"]+)"/g
const TOC_ID = /"#([\d]+_Toc[\d]+|[a-zA-Z0-9]{10})"/

const Xml = ({
  aol: _aol,
  sectionId = '',
  component = 'span',
  className = '',
  styles,
  render,
  id = '',
  eventCategory = '',
  caseRefMap = {}
}) => {
  const containerRef = useRef()
  const flavour = useSelector(selectFlavour)
  const aol = useSelector(selectAolName)
  const resolvedAol = typeof _aol === 'string' ? _aol : aol
  const textFragment = useSelector(selectTextFragment)
  const highlight = useSelector(selectHighlight)
  const pageId = useSelector(selectPageId)
  const { isMobile } = useSelector(selectDeviceType)
  const isTextFragmentTarget = !!textFragment && !!id && highlight === '#'.concat(id)

  useEffect(() => {
    [...containerRef.current.querySelectorAll('[class^=\'icon-\']')]
      .forEach(iconContainer => {
        const [name] = iconContainer.className.match(/icon-[^\s]+/)
        const root = createRoot(iconContainer)
        root.render(<Icon name={name} />)
      })
  }, [containerRef])

  useEffect(() => {
    if (!isTextFragmentTarget || !containerRef.current) return
    const mark = markText(containerRef.current, textFragment)
    if (!mark) return
    const timer = setTimeout(
      () => scrollTo({ pageId, element: mark, animate: true, useWindowScrollTo: isMobile }),
      100
    )
    return () => {
      clearTimeout(timer)
      if (containerRef.current) unmarkText(containerRef.current)
    }
  }, [isTextFragmentTarget, textFragment, render, pageId, isMobile])

  const __html = render.replace(DOCUMENT_HREF, (phrase) => {
    if (INTERNAL_DOCUMENT_HREF.test(phrase)) {
      return phrase.replace(
        INTERNAL_DOCUMENT_HREF,
        (phrase, focusedId) => `href="${url(QA_CONTENT, { aol: resolvedAol, flavour, sectionId, focusedId })}"`
      )
    } else if (TOC_ID.test(phrase)) {
      return phrase.replace(TOC_ID, (phrase, focusedId) => `"${url(QA_CONTENT, { aol, flavour, sectionId, focusedId })}"`)
    } else {
      return `${phrase} target="_blank" rel="noopener noreferrer"`
    }
  })

  const html = parseHtmlContent(__html, { aol: eventCategory === 'CA' ? resolvedAol : undefined, caseRefMap })

  if (component === 'div') {
    return (
      <div ref={containerRef} style={styles} className={className}>
        {html}
      </div>
    )
  } else {
    return (
      <span ref={containerRef} style={styles} className={className}>
        {html}
      </span>
    )
  }
}

Xml.propTypes = {
  render: PropTypes.string.isRequired,
  styles: PropTypes.any
}

export default Xml
